import React, { useEffect, useState } from "react";
import { api, fmtBRL, fmtDate, fmtDateOnly, CATEGORIA_LABEL } from "@/lib/api";
import { StatCard, SectionTitle, Badge } from "@/components/Brutal";
import { AlertTriangle, ClipboardList, Users, HardHat, ArrowDownToLine, ArrowUpFromLine, Package } from "lucide-react";
import { Link } from "react-router-dom";

const LOGO_URL = "https://customer-assets-39nsmqrw.emergentagent.net/job_motoserra-depot/artifacts/18uda418_logo%20Padr%C3%A3o.png";
const AERIAL_URL = "https://customer-assets-39nsmqrw.emergentagent.net/job_motoserra-depot/artifacts/25zvi9bh_DCIM_100MEDIA_DJI0679.JPG.webp";

export default function Dashboard() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setS(r.data)).finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="font-mono-data text-sm text-[#555]" data-testid="loading">Carregando painel...</div>;
  if (!s) return null;

  return (
    <div className="space-y-8" data-testid="dashboard">
      <div
        className="brutal-card relative overflow-hidden border-2 border-[#111]"
        style={{
          backgroundImage: `url('${AERIAL_URL}')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          minHeight: "280px",
        }}
      >
        {/* Overlay escuro para legibilidade */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A3C1E]/90 via-[#0A3C1E]/70 to-[#0A3C1E]/85" aria-hidden="true" />
        {/* Textura diagonal */}
        <div
          className="absolute inset-0 opacity-20 pointer-events-none"
          style={{ backgroundImage: "repeating-linear-gradient(45deg, transparent 0 22px, rgba(255,255,255,0.08) 22px 23px)" }}
          aria-hidden="true"
        />

        <div className="relative p-6 lg:p-10 flex flex-col lg:flex-row lg:items-center gap-6">
          <img
            src={LOGO_URL}
            alt="COOMFLONA"
            className="w-32 h-32 lg:w-40 lg:h-40 object-contain shrink-0 drop-shadow-2xl"
          />
          <div className="flex-1 min-w-0">
            <div className="font-mono-data text-[11px] uppercase tracking-[0.25em] text-[#F3C84B] mb-1">Painel COOMFLONA</div>
            <h2 className="font-display font-black text-3xl lg:text-5xl uppercase tracking-tighter leading-[0.95] text-white drop-shadow-lg">
              Almoxarifado<br />Florestal
            </h2>
            <p className="text-sm text-white/90 mt-3 max-w-md leading-relaxed">
              Estoque, EPIs, compras das comunidades, equipamentos e requisições em um só lugar.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 shrink-0">
            <Link to="/requisicoes" className="brutal-btn bg-[#FF4F00] text-white px-5 py-3 font-display font-bold uppercase text-xs tracking-widest">Nova Requisição</Link>
            <Link to="/produtos" className="brutal-btn bg-white text-[#111] px-5 py-3 font-display font-bold uppercase text-xs tracking-widest">Estoque</Link>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Produtos cadastrados" value={s.total_products}
          sub={<span><Package className="w-3 h-3 inline mr-1" />{fmtBRL(s.total_stock_value)} em estoque</span>} testid="stat-products" />
        <StatCard label="Estoque baixo" value={s.low_stock_count}
          sub={s.low_stock_count > 0 ? "⚠ Reposição urgente" : "Tudo ok"}
          accent={s.low_stock_count > 0} testid="stat-low-stock" />
        <StatCard label="EPIs a vencer (30d)" value={s.epis_a_vencer_count}
          sub={<span>{s.epis_vencidos_count} já vencidos</span>}
          danger={s.epis_vencidos_count > 0} testid="stat-epis" />
        <StatCard label="Requisições pendentes" value={s.pending_reqs}
          sub={<span><ClipboardList className="w-3 h-3 inline mr-1" />Aguardando aprovação</span>}
          testid="stat-reqs" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Entradas hoje" value={s.entradas_hoje}
          sub={<span><ArrowDownToLine className="w-3 h-3 inline mr-1" />Movimentações</span>} testid="stat-entradas" />
        <StatCard label="Saídas hoje" value={s.saidas_hoje}
          sub={<span><ArrowUpFromLine className="w-3 h-3 inline mr-1" />Movimentações</span>} testid="stat-saidas" />
        <StatCard label="Colaboradores" value={s.total_collabs}
          sub={<span><Users className="w-3 h-3 inline mr-1" />Cadastrados</span>} testid="stat-collabs" />
        <StatCard label="Cooperados / Comunitários" value={s.total_cooperados}
          sub={<span><HardHat className="w-3 h-3 inline mr-1" />Da rede</span>} testid="stat-cooperados" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="brutal-card">
          <div className="bg-[#3D6B57] text-white px-5 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-[#FF4F00]" /><span className="font-display font-bold uppercase text-sm tracking-wider">Estoque Crítico</span></div>
            <Badge variant="orange">{s.low_stock_count}</Badge>
          </div>
          <div className="divide-y divide-[#eee] max-h-80 overflow-auto" data-testid="low-stock-list">
            {s.low_stock_items.length === 0 && (
              <div className="p-6 text-center text-sm text-[#777] font-mono-data">Nenhuma peça em estoque crítico.</div>
            )}
            {s.low_stock_items.map((p) => (
              <div key={p.id} className="p-3 px-5 flex items-center justify-between">
                <div>
                  <div className="font-mono-data text-xs text-[#777]">{p.codigo} · {CATEGORIA_LABEL[p.categoria]}</div>
                  <div className="font-medium text-sm">{p.nome}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono-data font-bold text-[#E60000]">{p.estoque_atual} {p.unidade}</div>
                  <div className="font-mono-data text-[10px] text-[#777]">min: {p.estoque_minimo}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="brutal-card">
          <div className="bg-[#3D6B57] text-white px-5 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2"><HardHat className="w-4 h-4 text-[#FF4F00]" /><span className="font-display font-bold uppercase text-sm tracking-wider">EPIs · Validade</span></div>
            <Badge variant={s.epis_vencidos_count > 0 ? "danger" : "warn"}>{s.epis_vencidos_count + s.epis_a_vencer_count}</Badge>
          </div>
          <div className="divide-y divide-[#eee] max-h-80 overflow-auto">
            {s.epis_vencidos.length === 0 && s.epis_a_vencer.length === 0 && (
              <div className="p-6 text-center text-sm text-[#777] font-mono-data">Nenhum EPI vencido ou a vencer em 30 dias.</div>
            )}
            {s.epis_vencidos.map((e) => (
              <div key={e.id} className="p-3 px-5 flex items-center justify-between">
                <div className="min-w-0">
                  <div className="font-mono-data text-xs text-[#E60000]">VENCIDO · {fmtDateOnly(e.data_validade)}</div>
                  <div className="font-medium text-sm truncate">{e.collaborator_nome}</div>
                  <div className="text-xs text-[#555] truncate">{e.product_nome}</div>
                </div>
                <Badge variant="danger">Vencido</Badge>
              </div>
            ))}
            {s.epis_a_vencer.map((e) => (
              <div key={e.id} className="p-3 px-5 flex items-center justify-between">
                <div className="min-w-0">
                  <div className="font-mono-data text-xs text-[#FFC107]">VENCE · {fmtDateOnly(e.data_validade)}</div>
                  <div className="font-medium text-sm truncate">{e.collaborator_nome}</div>
                  <div className="text-xs text-[#555] truncate">{e.product_nome}</div>
                </div>
                <Badge variant="warn">30d</Badge>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div>
        <SectionTitle>Movimentações Recentes</SectionTitle>
        <div className="brutal-card overflow-x-auto">
          <table className="brutal-table">
            <thead><tr><th>Data</th><th>Tipo</th><th>Código</th><th>Produto</th><th className="text-right">Qtd</th><th>Motivo</th><th>Responsável</th></tr></thead>
            <tbody>
              {s.recent_movements.length === 0 && (<tr><td colSpan={7} className="text-center text-[#777] py-8 font-mono-data text-sm">Nenhuma movimentação.</td></tr>)}
              {s.recent_movements.map((m) => (
                <tr key={m.id}>
                  <td className="font-mono-data text-xs">{fmtDate(m.created_at)}</td>
                  <td>{m.tipo === "entrada" ? <Badge variant="success">↑ Entrada</Badge> : <Badge variant="orange">↓ Saída</Badge>}</td>
                  <td className="font-mono-data text-xs">{m.product_codigo}</td>
                  <td>{m.product_nome}</td>
                  <td className="text-right font-mono-data font-bold">{m.quantidade}</td>
                  <td className="text-sm text-[#555]">{m.motivo || "—"}</td>
                  <td className="text-xs text-[#555]">{m.responsavel_nome || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
